import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, errMsg, inr, num, refLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EntityForm from "@/components/EntityForm";
import { Badge2, ProjectSelect } from "@/components/common";

const REF_URL = { projects: "/projects", users: "/team", leads: "/leads", inventory: "/inventory", channel_partners: "/channel_partners" };

export function useRefs(fields) {
  const [refs, setRefs] = useState({});
  const key = (fields || []).map((f) => f.type).join();
  useEffect(() => {
    const types = [...new Set((fields || []).filter((f) => f.type.startsWith("ref:")).map((f) => f.type.slice(4)))];
    Promise.all(types.map((t) => api.get(REF_URL[t]).then((r) => [t, r.data]).catch(() => [t, []]))).then((pairs) => setRefs(Object.fromEntries(pairs)));
  }, [key]); // eslint-disable-line
  return refs;
}

const MONEY = /amount|price|budget|spend|reward/;

export function renderCell(f, v, refs) {
  if (v == null || v === "") return <span className="text-slate-600">-</span>;
  if (f.type.startsWith("ref:")) return refLabel((refs[f.type.slice(4)] || []).find((r) => r.id === v));
  if (f.type === "select" && (f.name === "status" || f.name === "stage")) return <Badge2>{v}</Badge2>;
  if (f.type === "number") return <span className="font-mono">{MONEY.test(f.name) ? inr(v) : num(v)}</span>;
  if (f.type === "datetime") return String(v).replace("T", " ").slice(0, 16);
  return String(v);
}

export default function EntityPage({ entity, rowActions, summary, filters = {}, hideProjectFilter, onRows, reloadKey, defaults, readOnly }) {
  const { schema, can, me } = useAuth();
  const cfg = schema?.entities[entity];
  const refs = useRefs(cfg?.fields);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, initial: null });
  const writable = !readOnly && cfg && (cfg.global ? me?.is_super_admin : can(cfg.module, "write"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, ...(projectId ? { project_id: projectId } : {}) };
      const r = await api.get(`/${entity}`, { params });
      setRows(r.data);
      onRows?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [entity, projectId, JSON.stringify(filters)]); // eslint-disable-line

  useEffect(() => { load(); }, [load, reloadKey]);

  const shown = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(s)));
  }, [rows, q]);

  if (!cfg) return null;
  const cols = cfg.fields.filter((f) => f.table);
  const hasProject = cfg.fields.some((f) => f.name === "project_id");

  const remove = async (row) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/${entity}/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid={`${entity}-page`}>
      {summary && summary(rows)}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-500" />
            <Input placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-56" data-testid={`${entity}-search-input`} />
          </div>
          {hasProject && !hideProjectFilter && <ProjectSelect allowAll value={projectId} onChange={setProjectId} testid={`${entity}-project-filter`} />}
          <span className="text-xs text-slate-500 font-mono" data-testid={`${entity}-count`}>{shown.length} records</span>
        </div>
        {writable && (
          <Button onClick={() => setForm({ open: true, initial: null })} data-testid={`${entity}-create-button`}><Plus className="h-4 w-4 mr-1" />New {cfg.label.replace(/s$/, "")}</Button>
        )}
      </div>
      <div className="rounded-xl border border-slate-800 overflow-x-auto custom-scrollbar bg-white">
        <table className="w-full text-left text-sm text-slate-300" data-testid={`${entity}-table`}>
          <thead className="bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>{cols.map((c) => <th key={c.name} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}<th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-slate-500">Loading...</td></tr>}
            {!loading && !shown.length && <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-slate-500">No records yet</td></tr>}
            {!loading && shown.map((r) => (
              <tr key={r.id} className="border-t border-slate-800/80 hover:bg-slate-800/30 transition-colors" data-testid={`${entity}-row-${r.id}`}>
                {cols.map((c) => <td key={c.name} className="px-4 py-3 whitespace-nowrap max-w-[260px] truncate">{renderCell(c, r[c.name], refs)}</td>)}
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {rowActions?.(r, load)}
                  {writable && <Button size="icon" variant="ghost" onClick={() => setForm({ open: true, initial: r })} data-testid={`${entity}-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {writable && entity !== "projects" && <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`${entity}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EntityForm entity={entity} refs={refs} open={form.open} initial={form.initial} defaults={defaults} onClose={() => setForm({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
